/**
 * Centralised HTTP client for the Capsule backend.
 *
 * Refactored from the legacy `api_client.js`. Behaviour that the live system
 * depended on is preserved exactly:
 *   - JWT is read from localStorage under `capsule_jwt`
 *   - every request is prefixed with the API base ("/api" by default)
 *   - a 401 clears the token and notifies the app so it can bounce to login
 *   - plain-object bodies are auto-serialised to JSON
 *
 * Removed (dead in the legacy file): `registerPatient`, `getPatients`,
 * `getDoctorById`, `getPackages`, `getPackageRequests`, `createPackageRequest`,
 * `updatePackageRequestStatus`, `createBooking`, `getDoctorBookings` — every one
 * of them pointed at an endpoint the backend does not expose.
 */

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

export const TOKEN_KEY = 'capsule_jwt';

/** Raised for any non-2xx response so callers can branch on `status`. */
export class ApiError extends Error {
  constructor(message, { status, endpoint, method, body } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.endpoint = endpoint;
    this.method = method;
    this.body = body;
  }
}

/* --------------------------------------------------------------------------
   Token storage
-------------------------------------------------------------------------- */

export const tokenStore = {
  get: () => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token) => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* storage unavailable (private mode) — requests simply run unauthenticated */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* no-op */
    }
  },
};

/* --------------------------------------------------------------------------
   The hospital the Super Admin is working inside

   The platform admin may open any hospital's dashboard and work there. Which
   hospital travels as the X-Institution-Id header, and the server honours it
   for the admin role only (rbacMiddleware.loadActor) — for anybody else it is
   ignored, so there is nothing here a staff account could use to reach next
   door. Kept in sessionStorage: it belongs to this tab's session, and a new
   tab starts back at the platform level.
-------------------------------------------------------------------------- */

const ACTING_KEY = 'capsule_acting_institution';

export const actingInstitution = {
  /** { id, name } or null */
  get: () => {
    try {
      const raw = sessionStorage.getItem(ACTING_KEY);
      const value = raw ? JSON.parse(raw) : null;
      return value && value.id ? value : null;
    } catch {
      return null;
    }
  },
  set: (institution) => {
    try {
      if (institution && institution.id) {
        sessionStorage.setItem(ACTING_KEY, JSON.stringify({ id: institution.id, name: institution.name || '' }));
      } else {
        sessionStorage.removeItem(ACTING_KEY);
      }
    } catch {
      /* storage unavailable — the admin simply stays at the platform level */
    }
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('capsule:acting-institution'));
  },
  clear: () => actingInstitution.set(null),
};

/* --------------------------------------------------------------------------
   Unauthorised handling

   The legacy client force-navigated on 401 and brute-force-cleared every
   interval id in the page. Here we just clear the token and emit an event; the
   AuthContext owns the redirect, and React owns its own timers.
-------------------------------------------------------------------------- */

export const UNAUTHORIZED_EVENT = 'capsule:unauthorized';

function handleUnauthorized(endpoint) {
  tokenStore.clear();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(UNAUTHORIZED_EVENT, { detail: { endpoint } }));
  }
}

/* --------------------------------------------------------------------------
   Core request
-------------------------------------------------------------------------- */

/**
 * @param {string} endpoint  Path relative to the API base, e.g. `/doctors/profile`.
 * @param {object} [options]
 * @param {string} [options.method]
 * @param {any}    [options.body]    Plain objects are JSON-stringified.
 * @param {object} [options.headers]
 * @param {boolean}[options.auth]    Set false to deliberately omit the token.
 * @returns {Promise<any>} Parsed JSON body (or `null` for an empty 204).
 */
export async function request(endpoint, options = {}) {
  const { method = 'GET', body, headers: extraHeaders, auth = true, ...rest } = options;

  const token = auth ? tokenStore.get() : null;
  const acting = auth ? actingInstitution.get() : null;
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(acting ? { 'X-Institution-Id': String(acting.id) } : {}),
    ...extraHeaders,
  };

  let payload = body;
  if (payload && typeof payload === 'object' && !(payload instanceof FormData)) {
    payload = JSON.stringify(payload);
  }
  if (payload instanceof FormData) {
    delete headers['Content-Type']; // let the browser set the multipart boundary
  }

  let response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...rest,
      method,
      headers,
      ...(payload !== undefined ? { body: payload } : {}),
    });
  } catch (networkError) {
    throw new ApiError(`Network request failed: ${networkError.message}`, {
      status: 0,
      endpoint,
      method,
    });
  }

  if (response.status === 204) return null;

  let data = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(endpoint);
    // The central control refused: the package may have changed under this
    // screen, so the session's capabilities are read again (PermissionContext).
    if (response.status === 403 && data && typeof data === 'object' && CONTROL_CODES.includes(data.code)) {
      announce(CONTROL_REFUSED_EVENT, data);
    }
    const message =
      (data && typeof data === 'object' && (data.error || data.message)) ||
      `Request failed with status ${response.status}`;
    throw new ApiError(message, { status: response.status, endpoint, method, body: data });
  }

  // A request that brought a limit to 80% or more says so in a header; the
  // shell turns it into a toast (components/layout/AppShell.jsx).
  const nearLimit = response.headers && response.headers.get && response.headers.get('X-Capsule-Limit');
  if (nearLimit) announce(LIMIT_EVENT, nearLimit);

  return data;
}

/* --------------------------------------------------------------------------
   The central control's two signals
-------------------------------------------------------------------------- */

export const LIMIT_EVENT = 'capsule:limit';
export const CONTROL_REFUSED_EVENT = 'capsule:control-refused';
const CONTROL_CODES = ['serviceClosed', 'serviceReadonly', 'serviceDraining', 'limitReached', 'roleNotInPackage'];

function announce(name, detail) {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(name, { detail }));
}

/**
 * Cache-busted GET — the legacy `requestFresh`, used by the refresh/polling
 * layer to defeat intermediate caches.
 */
export function requestFresh(endpoint, options = {}) {
  const separator = endpoint.includes('?') ? '&' : '?';
  return request(`${endpoint}${separator}t=${Date.now()}`, {
    ...options,
    headers: { 'Cache-Control': 'no-cache', ...options.headers },
  });
}

/** The auth headers every request carries — for the two helpers below that bypass `request`. */
function authHeaders() {
  const token = tokenStore.get();
  const acting = actingInstitution.get();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(acting ? { 'X-Institution-Id': String(acting.id) } : {}),
  };
}

/**
 * Upload a File as the raw request body, reporting progress.
 *
 * XMLHttpRequest rather than fetch because fetch still cannot report upload
 * progress, and a 50 MB scan over a clinic's connection needs a bar that
 * moves. The body is always application/octet-stream so the server never
 * mistakes a JSON file for a request; the real type travels in X-File-Type.
 *
 * @param {string} endpoint
 * @param {Blob} file
 * @param {object} [options]
 * @param {object} [options.headers]     extra headers (values already encoded)
 * @param {(fraction: number) => void} [options.onProgress]
 * @param {AbortSignal} [options.signal]
 */
export function uploadBinary(endpoint, file, { headers = {}, onProgress, signal } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}${endpoint}`);
    const all = { ...authHeaders(), 'Content-Type': 'application/octet-stream', ...headers };
    Object.entries(all).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress(event.loaded / event.total);
      };
    }
    if (signal) signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.onerror = () => reject(new ApiError('Network request failed', { status: 0, endpoint, method: 'POST' }));
    xhr.onabort = () => reject(new ApiError('تم إلغاء الرفع', { status: 0, endpoint, method: 'POST' }));
    xhr.onload = () => {
      let data = null;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        data = xhr.responseText;
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(data);
      if (xhr.status === 401) handleUnauthorized(endpoint);
      const message =
        (data && typeof data === 'object' && (data.error || data.message)) ||
        (xhr.status === 413 ? 'حجم الملف أكبر من المسموح' : `Request failed with status ${xhr.status}`);
      return reject(new ApiError(message, { status: xhr.status, endpoint, method: 'POST', body: data }));
    };
    xhr.send(file);
  });
}

/**
 * GET a binary resource with the session's token, as a Blob.
 *
 * The attachment bytes are never reachable by a bare URL — an <img src> or a
 * new tab carries no bearer token — so the viewer fetches here and shows the
 * result from a blob: URL it revokes when it closes.
 */
export async function fetchBlob(endpoint, { signal } = {}) {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, { headers: authHeaders(), signal });
  } catch (networkError) {
    throw new ApiError(`Network request failed: ${networkError.message}`, { status: 0, endpoint, method: 'GET' });
  }
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(endpoint);
    let message = `Request failed with status ${response.status}`;
    try {
      const data = await response.json();
      message = data.error || message;
    } catch {
      /* not JSON */
    }
    throw new ApiError(message, { status: response.status, endpoint, method: 'GET' });
  }
  return response.blob();
}

/**
 * POST a body and take a file back (an export): the blob, and what the server
 * said about it in its headers. A refusal reads like any other request's.
 */
export async function postBlob(endpoint, body) {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch (networkError) {
    throw new ApiError(`Network request failed: ${networkError.message}`, { status: 0, endpoint, method: 'POST' });
  }
  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(endpoint);
    let data = null;
    try {
      data = await response.json();
    } catch {
      /* not JSON */
    }
    if (response.status === 403 && data && CONTROL_CODES.includes(data.code)) announce(CONTROL_REFUSED_EVENT, data);
    throw new ApiError((data && data.error) || `Request failed with status ${response.status}`, { status: response.status, endpoint, method: 'POST', body: data });
  }
  return { blob: await response.blob(), rows: Number(response.headers.get('X-Export-Rows') || 0), masked: response.headers.get('X-Export-Masked') === '1' };
}

/** Build a query string from a params object, dropping null/undefined/''. */
export function qs(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.append(key, value);
  });
  const str = search.toString();
  return str ? `?${str}` : '';
}

export { API_BASE_URL };
